// Tool/technology logos used in the "Delivery Architecture" stack matrix.
// Render logos with <ToolLogo> (uses <img src>, never inline SVG — several of
// these files share clipPath ids and would clip each other if imported as
// React components on the same page).

import aiBuilder from '../assets/tools/ai-builder.svg';
import azureAiServices from '../assets/tools/azure-ai-services.svg';
import azureDataFactory from '../assets/tools/azure-data-factory.svg';
import azureDevops from '../assets/tools/azure-devops.svg';
import azureOpenai from '../assets/tools/azure-openai.svg';
import claude from '../assets/tools/claude.svg';
import copilotStudio from '../assets/tools/copilot-studio.svg';
import dataverse from '../assets/tools/dataverse.svg';
import fabric from '../assets/tools/fabric.svg';
import gemini from '../assets/tools/gemini.svg';
import logicApps from '../assets/tools/logic-apps.svg';
import make from '../assets/tools/make.svg';
import microsoft365 from '../assets/tools/microsoft-365.png';
import msProject from '../assets/tools/ms-project.svg';
import n8n from '../assets/tools/n8n.svg';
import openai from '../assets/tools/openai.svg';
import powerAutomate from '../assets/tools/power-automate.svg';
import powerBi from '../assets/tools/power-bi.svg';
import powerPlatform from '../assets/tools/power-platform.svg';
import uipath from '../assets/tools/uipath.svg';
import { IconId } from '../components/icons';

export type ToolId =
  | 'copilot-studio'
  | 'power-automate'
  | 'power-platform'
  | 'power-bi'
  | 'dataverse'
  | 'fabric'
  | 'ai-builder'
  | 'azure-ai-services'
  | 'azure-data-factory'
  | 'azure-devops'
  | 'ms-project'
  | 'claude'
  | 'gemini'
  | 'openai'
  | 'uipath'
  | 'n8n'
  | 'make'
  | 'azure-openai'
  | 'logic-apps'
  | 'microsoft-365';

export interface Tool {
  id: ToolId;
  name: string;
  /** Logo image URL. When absent, ToolLogo renders a monogram fallback chip. */
  logo?: string;
  /** 1-3 character fallback shown when no logo is available. */
  monogram?: string;
}

export const TOOLS: Record<ToolId, Tool> = {
  'copilot-studio': { id: 'copilot-studio', name: 'Copilot Studio', logo: copilotStudio },
  'power-automate': { id: 'power-automate', name: 'Power Automate', logo: powerAutomate },
  'power-platform': { id: 'power-platform', name: 'Power Platform', logo: powerPlatform },
  'power-bi': { id: 'power-bi', name: 'Power BI', logo: powerBi },
  dataverse: { id: 'dataverse', name: 'Dataverse', logo: dataverse },
  fabric: { id: 'fabric', name: 'Microsoft Fabric', logo: fabric },
  'ai-builder': { id: 'ai-builder', name: 'AI Builder', logo: aiBuilder },
  'azure-ai-services': {
    id: 'azure-ai-services',
    name: 'Azure AI Services',
    logo: azureAiServices,
  },
  'azure-data-factory': {
    id: 'azure-data-factory',
    name: 'Azure Data Factory',
    logo: azureDataFactory,
  },
  'azure-devops': { id: 'azure-devops', name: 'Azure DevOps', logo: azureDevops },
  'ms-project': { id: 'ms-project', name: 'Microsoft Project', logo: msProject },
  claude: { id: 'claude', name: 'Claude', logo: claude },
  gemini: { id: 'gemini', name: 'Gemini', logo: gemini },
  openai: { id: 'openai', name: 'OpenAI', logo: openai },
  uipath: { id: 'uipath', name: 'UiPath', logo: uipath },
  n8n: { id: 'n8n', name: 'n8n', logo: n8n },
  make: { id: 'make', name: 'Make', logo: make },
  'azure-openai': { id: 'azure-openai', name: 'Azure OpenAI', logo: azureOpenai },
  'logic-apps': { id: 'logic-apps', name: 'Azure Logic Apps', logo: logicApps },
  'microsoft-365': { id: 'microsoft-365', name: 'Microsoft 365', logo: microsoft365 },
};

export interface StackLayer {
  id: string;
  name: string;
  description: string;
  icon: IconId;
  tools: ToolId[];
}

/** "Delivery Architecture" matrix — grouped by capability layer, Chiacon-style. */
export const STACK_LAYERS: StackLayer[] = [
  {
    id: 'ai-agents',
    name: 'AI & Agents',
    description: 'Where models reason, retrieve, and act',
    icon: 'layer-ai',
    tools: [
      'copilot-studio',
      'azure-ai-services',
      'azure-openai',
      'ai-builder',
      'openai',
      'claude',
      'gemini',
    ],
  },
  {
    id: 'automation',
    name: 'Automation & Integration',
    description: 'Where workflows trigger, route, and execute',
    icon: 'layer-automation',
    tools: ['power-automate', 'n8n', 'logic-apps', 'make', 'uipath'],
  },
  {
    id: 'apps-data',
    name: 'Apps & Data',
    description: 'The governed substrate every workflow runs on',
    icon: 'layer-data',
    tools: ['power-platform', 'dataverse', 'power-bi', 'fabric', 'azure-data-factory'],
  },
  {
    id: 'delivery',
    name: 'Delivery & Governance',
    description: 'How work is planned, tracked, and rolled out',
    icon: 'layer-delivery',
    tools: ['azure-devops', 'ms-project', 'microsoft-365'],
  },
];
